// ============================================================================
// NavActions
// ----------------------------------------------------------------------------
// The QuoteMaker top-nav RIGHT action cluster: the AI-pipeline status pill +
// STOP button, the Stage-5 (NEW PROJECT / back-to-scope / SAVE) actions, the
// passive auto-save status, the NEW button, and the BACK / NEXT navigation pair
// (whose label / disabled / aria / title derive from the current stage +
// pipeline state). Purely presentational — every state value + handler arrives
// as a prop named identically to its App identifier, so the JSX body is a
// byte-identical verbatim move; the component composes the already-extracted
// AiPipelineStatus + StopAiButton leaves.
//
// TS-060 / DEC-COUNCIL-015 T3 (QuoteMakerNavBar sub-slice): extracted VERBATIM
// from App.jsx's top-nav render (behaviour-preserving; only type annotations +
// this header added — ZERO identifier renames). No pipeline state moves in.
// ============================================================================

import React from 'react';
import { Icons } from './Icon';
import { STAGES } from '../../config/brand';
import { AiPipelineStatus } from './AiPipelineStatus';
import { StopAiButton } from './StopAiButton';
import { ThematicSaveControl } from './ThematicSaveControl';

interface NavActionsProps {
    isAnalyzing: boolean;
    isSummarizing: boolean;
    isGenerating: boolean;
    photoAssignBusy: boolean;
    projectWMSGenerating: boolean;
    hasLivePipelineEntry: boolean;
    quoteEditorOpen: boolean;
    quoteEditorSaveState?: { dirty?: boolean; saving?: boolean; saved?: boolean } | null;
    isSavingProject: boolean;
    hasUnsavedChanges?: boolean;
    onManualSave?: () => void;
    isStage1Processing: boolean;
    isFastTrackManualScope: boolean;
    /**
     * Stage 1 on a Quote Brain job (quoteBrainStage1Route): 'writing' hides the forward button (the run banner is the way
     * on), except on Fast-Track, whose button is the manual route to the Scope Builder; 'written' makes it SCOPE, which
     * opens the items. Otherwise it is SCOPE BUILDER.
     */
    quoteBrainRoute?: 'writing' | 'written' | null;
    isNextStageJustReady: boolean;
    stage: number;
    quoteData: unknown;
    analysis: unknown;
    /** App's last-saved timestamp (a Date) or null; read only for truthiness in the auto-save gate. */
    lastSavedAt: Date | null;
    projectWMS: { structured?: unknown } | null | undefined;
    aiPipelineCancelToast: { message: string } | null;
    canGoNext: () => boolean;
    cancelAiPipeline: (opts: { reason: string }) => void;
    handleBack: () => void;
    handleNext: () => void;
    handleQuoteEditorBack: () => void;
    openNewProjectWorkflowChooser: () => void;
    quoteEditorActionsRef: React.MutableRefObject<{ save?: () => void }>;
}

export function NavActions({
    isAnalyzing,
    isSummarizing,
    isGenerating,
    photoAssignBusy,
    projectWMSGenerating,
    hasLivePipelineEntry,
    quoteEditorOpen,
    quoteEditorSaveState = null,
    isSavingProject,
    hasUnsavedChanges = false,
    onManualSave,
    isStage1Processing,
    isFastTrackManualScope,
    quoteBrainRoute = null,
    isNextStageJustReady,
    stage,
    quoteData,
    analysis,
    lastSavedAt,
    projectWMS,
    aiPipelineCancelToast,
    canGoNext,
    cancelAiPipeline,
    handleBack,
    handleNext,
    handleQuoteEditorBack,
    openNewProjectWorkflowChooser,
    quoteEditorActionsRef,
}: NavActionsProps) {
    // 5B / O-F12: the preview's Back returns to the stage the preview was opened FROM. A Stage 5 editor returns to Scope Builder; a quote
    // previewed from the Work Method (a peek leaves the stage at 3 and closes back to it) returns to the Work Method. The label says which.
    const quotePreviewBackLabel = stage === 3 ? 'Back to Work Method' : 'Back to Scope Builder';
    // 5C / O-F9: Stage 2 waits on Risk only when there is no usable prior Method to review. This is the predicate that disables Next, so the wait
    // wording (title, accessible name, spinner label) is shown exactly when Next is disabled for that reason, never on an enabled button.
    const waitsForFirstRisk = stage === 2 && isAnalyzing && !projectWMS?.structured;
    // 5B / O-F11: the raw-stage Back names the stage before this one, which is where Back goes EXCEPT on the Fast-Track route, where
    // Scope came straight from Stage 1 and Back returns there (use-stage-navigation handleBack). Stage 1 has no Back.
    // Scope comes straight from Stage 1 on every job now (docs/old-pipeline-removal/PLAN.md).
    const rawBackLabel = stage === 2 || stage === 4 ? 'Back to Job details'
            : `Back to ${STAGES[stage - 2]?.name || 'previous stage'}`;
    return (
        <div className="qm-nav-actions flex items-center gap-2 relative z-10 shrink-0">
            {/* v5.19.24 (split 2026-05-06): AI-pipeline LIVE
                info pill. Renders only when something AI-side
                is in flight (analysis, summary, photo-assign,
                WMS, costing, quote gen). The STOP button used
                to live INSIDE this pill but was relocated to
                the far right of this action cluster (past NEXT)
                so the destructive cancel affordance is visually
                separated from the inline status indicator. See
                the STOP block AFTER the NEXT button. */}
            <AiPipelineStatus
                isAnalyzing={isAnalyzing}
                isSummarizing={isSummarizing}
                isGenerating={isGenerating}
                photoAssignBusy={photoAssignBusy}
                projectWMSGenerating={projectWMSGenerating}
                aiPipelineCancelToast={aiPipelineCancelToast}
            />

            {/* STOP-AI button \u2014 sits right beside the AI LIVE pill
                (far left of this action cluster) so the destructive
                cancel can't be misclicked for NEXT. Gated on the same
                PipelineMonitor entry (hasLivePipelineEntry) as before;
                .btn-nav-new.accent-red keeps its 30px height in line
                with the pill + the other nav buttons. */}
            <StopAiButton
                hasLivePipelineEntry={hasLivePipelineEntry}
                isAnalyzing={isAnalyzing}
                isSummarizing={isSummarizing}
                projectWMSGenerating={projectWMSGenerating}
                photoAssignBusy={photoAssignBusy}
                isGenerating={isGenerating}
                onStop={() => cancelAiPipeline({ reason: 'user-stop-button' })}
            />

            {/* Stage 5 (Final Quote) action buttons \u2014 quote editor is open */}
            {quoteEditorOpen && (
            <>
                <button
                    onClick={() => openNewProjectWorkflowChooser()}
                    className="btn-nav-new qm-nav-new qm-nav-stage5-action qm-nav-stage5-action--new"
                    aria-label="Start a new project"
                    title="Save this quote and start a fresh project"
                >
                    <Icons.Plus size={13} />
                    <span>NEW</span>
                </button>
                <button
                    onClick={handleQuoteEditorBack}
                    className="btn-nav-new btn-nav-icon qm-nav-stage5-action qm-nav-stage5-action--back"
                    aria-label={quotePreviewBackLabel}
                    title={quotePreviewBackLabel}
                >
                    <Icons.ArrowLeft size={16} aria-hidden="true" />
                </button>
                <ThematicSaveControl
                    isSaving={Boolean(quoteEditorSaveState?.saving)}
                    hasUnsavedChanges={quoteEditorSaveState ? Boolean(quoteEditorSaveState.dirty) : true}
                    disabled={isGenerating}
                    disabledTitle="The prior quote is read-only while its replacement is generating"
                    onSave={() => quoteEditorActionsRef.current.save?.()}
                    variant="stage5"
                />
            </>
            )}

            {/* Thematic Save control (compact, responsive, interactive) —
                replaces the wide text pill with a 32px industrial save icon button.
                Shows SAVED (emerald) when committed, SAVING (amber sync) when writing,
                and SAVE (brand orange) when unsaved changes are present. Clicking triggers
                immediate project persistence. */}
            {!quoteEditorOpen && (isSavingProject || lastSavedAt || hasUnsavedChanges) && (
                <ThematicSaveControl
                    isSaving={isSavingProject}
                    hasUnsavedChanges={hasUnsavedChanges}
                    lastSavedAt={lastSavedAt}
                    onSave={onManualSave}
                    variant="navbar"
                />
            )}

            {/* NEW button (all stages) */}
            {!quoteEditorOpen && (
            <button
                onClick={() => openNewProjectWorkflowChooser()}
                className="btn-nav-new qm-nav-new"
                title="Clear all fields and start a fresh project"
            >
                <Icons.Plus size={13} />
                <span>NEW</span>
            </button>
            )}

            {/* RE-ANALYSE button removed (Peter v5.41.194) — an
                unnecessary top-nav escape hatch. Re-running analysis is
                already covered: NEXT shows a "Re-run Risk Analysis?"
                confirm when the data changed since the last run, the
                summary's Out of date note offers Refresh, and
                Stage 2 has its own re-analyse. Don't re-add. */}

            {/* REGEN button removed (Peter, deterministic-render rework):
                Stage 5 mirrors Stage 4 scope + Stage 1 metadata, so
                regenerating the Final Quote from the Stage 4 nav has
                no purpose. The underlying generateQuote() is still
                used for the initial Stage 4 → 5 transition when no
                quoteData exists yet. */}

            {/* BACK + NEXT adjacent cluster - primary navigation pair
                renders at the far right so the pair stays together on
                every viewport. BACK renders on stages 2–5 (never on
                Stage 1, the first screen). */}
            {!quoteEditorOpen && stage > 1 && (
                <button
                    onClick={handleBack}
                    className="btn-nav-new btn-nav-icon"
                    aria-label={rawBackLabel}
                    title={rawBackLabel}
                >
                    <Icons.ArrowLeft size={16} aria-hidden="true" />
                </button>
            )}

            {/* NEXT / Generate button
              *
              * Render gate: the isStage1Processing lockout only applies while
              * the user is ACTUALLY on Stage 1 (Data Entry). On stages 2, 3, 4
              * the NEXT button must always be considered for render regardless
              * of any stale Stage 1 processing flag - otherwise a stuck flag
              * from a Stage 1 unmount leaves the user with no way forward.
              *
              * Label: on Stage 1 one press does the rest (it makes or
              * refreshes the summary first when it needs to), then opens the
              * Scope Builder: "SCOPE BUILDER". The Risk report and the Work
              * Method went with the old pipeline (docs/old-pipeline-removal).
              * On a QuoteMaker job the button is hidden while it writes, and
              * says "SCOPE" once it has written.
              */}
            {!quoteEditorOpen && stage < 5 && !(stage === 1 && isStage1Processing) && !(stage === 1 && quoteBrainRoute === 'writing' && !isFastTrackManualScope) && (
                <button
                    onClick={handleNext}
                    disabled={
                        // Stage 2 (Risk Report) while analysis is running.
                        // Original v5.5 rule (Peter 2026-04-09): disabled —
                        // user must wait for fresh analysis before advancing.
                        // v5.19.17 (Peter 2026-05-05) refinement: ONLY disable
                        // when this is a FIRST analysis (no prior projectWMS).
                        // During RE-ANALYSE on a previously-quoted project,
                        // Method/Scope/Quote already exist and the user must
                        // be able to navigate forward to view them — same as
                        // clicking the green-ticked stage in the strip.
                        waitsForFirstRisk ||
                        // Stage 1 + analysis in progress: enabled (button
                        // label says "View Live Analysis" so the user can
                        // navigate to the streaming view).
                        // Stage 1 + analysis ready: enabled.
                        // Other stages: disabled when canGoNext is false.
                        (!canGoNext() && !(stage === 1 && (isAnalyzing || analysis))) ||
                        // Stage 4 opens Stage 5 from live scope, so background
                        // quote generation no longer disables this navigation action.
                        // isSummarizing only disables on Stage 1, where the
                        // press would otherwise start a second summary.
                        (isSummarizing && stage === 1)
                    }
                    className={`btn-next-stage ${isAnalyzing ? 'is-analyzing' : ''} ${isNextStageJustReady ? 'is-just-ready' : ''}`}
                    // On Stage 1 every name starts with the word the button shows (WCAG 2.5.3 Label in Name), so a
                    // voice user who says "Analyse" reaches it. Pinned in nav-actions.test.tsx.
                    aria-label={
                        waitsForFirstRisk ? 'Risk Report is still analysing' :
                        isStage1Processing && stage === 1 ? 'Files still processing' :
                        isSummarizing && stage === 1 ? 'Processing, writing the summary' :
                        stage === 1 && quoteBrainRoute === 'written' ? 'Scope, open QuoteMaker\'s items' :
                        stage === 1 ? 'Scope Builder, write the scope' :
                        stage === 2 ? 'Continue to Work Method' :
                        stage === 3 ? 'Continue to Scope Builder' :
                        stage === 4 ? 'Open final quote view' :
                        quoteData ? 'Continue to quote editor' : 'Open final quote view'
                    }
                    title={
                        waitsForFirstRisk ? 'Risk Report is still analysing \u2014 please wait' :
                        isStage1Processing && stage === 1 ? 'Please wait, files are still being processed' :
                        isSummarizing && stage === 1 ? 'Processing, writing the summary' :
                        stage === 1 && quoteBrainRoute === 'written' ? 'Scope, open QuoteMaker\'s items' :
                        stage === 1 ? 'Scope Builder, write the scope' :
                        stage === 2 ? 'Continue to Work Method' :
                        stage === 3 ? 'Continue to Scope Builder' :
                        stage === 4 ? 'Open final quote view' :
                        quoteData ? 'Continue to quote editor' : 'Open final quote view'
                    }
                >
                    {(() => {
                        // v5.24.21 (Peter 2026-05-16): scope pipeline-status labels
                        // to the stage where the click action IS that pipeline.
                        // Background auto-draft / re-summary shouldn't repaint the
                        // navigation button with a misleading "Recalling learnings"
                        // / "Processing" label while the top-nav stage strip shows
                        // the next stage as already navigable. Pair this with the
                        // disabled rules above.
                        if (isSummarizing && stage === 1) return <><Icons.Loader2 size={14} className="animate-spin" /><span>Processing</span></>;
                        // On a phone the label is "Live" (top-navbar.css); the button's aria-label names it in full.
                        // Stage 2 first-analysis lock (no prior WMS): label as
                        // "Analysing" so the spinner matches the disabled state.
                        // On stages 3+ or Stage 2 with prior WMS, isAnalyzing is
                        // a background re-analyse — show plain NEXT instead.
                        if (waitsForFirstRisk) return <><Icons.Loader2 size={14} className="animate-spin" /><span>Analysing</span></>;
                        // v5.40.3 (UX audit Wave-4 #1): every NEXT-button state gets
                        // a label. Pre-fix the "plain navigation" cases (e.g. Stage 2 → 3,
                        // Stage 3 → 4, Stage 1.5 with fresh analysis) returned `null` so
                        // the button rendered as a bare `→` arrow. Two adjacent icon-only
                        // buttons (BACK + forward) confused first-time orientation. Now
                        // the fallback label is "NEXT" — the orange brand-coloured
                        // primary action always says what it does.
                        const actionLabel =
                            stage === 1 && quoteBrainRoute === 'written' ? 'SCOPE' :
                            stage === 1 ? 'SCOPE BUILDER' :
                            stage === 4 ? 'QUOTE' :
                            'NEXT';
                        return (
                            <><span>{actionLabel}</span><Icons.ArrowRight size={14} /></>
                        );
                    })()}
                </button>
            )}

            {/* STOP-AI button relocated beside the AI LIVE pill
                (far left of this cluster) so the destructive
                cancel can't be misclicked for NEXT. See the pill
                block above. */}
        </div>
    );
}
